import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Home, Search, MapPin, Euro, Bed, Bath, Square, ArrowLeft, Heart, Phone, Mail, Filter, Grid, List, ChevronRight, Star, Shield, Users } from 'lucide-react';
import { toast } from 'sonner';

const ImmobilienDemo = () => {
  const [viewMode, setViewMode] = useState('grid');
  const [favorites, setFavorites] = useState([]);
  
  const handleSearch = (e) => {
    e.preventDefault();
    toast.success('🔍 Suche gestartet', {
      description: '125 Immobilien gefunden'
    });
  };
  
  const handleFavorite = (id) => {
    if (favorites.includes(id)) {
      setFavorites(favorites.filter(f => f !== id));
      toast.info('❤️ Von Favoriten entfernt');
    } else {
      setFavorites([...favorites, id]);
      toast.success('❤️ Zu Favoriten hinzugefügt');
    }
  };

  const properties = [
    { id: 1, title: 'Penthouse Berlin Mitte', price: '1.250.000', rooms: 4, baths: 2, size: 145, img: 'https://images.unsplash.com/photo-1560518883-ce09059eeffa?w=600&q=80', type: 'Kaufen', location: 'Berlin Mitte', has360: true },
    { id: 2, title: 'Altbau Charlottenburg', price: '890.000', rooms: 3, baths: 1, size: 98, img: 'https://images.unsplash.com/photo-1502672260266-1c1ef2d93688?w=600&q=80', type: 'Kaufen', location: 'Charlottenburg', has360: true },
    { id: 3, title: 'Neubau Prenzlauer Berg', price: '650.000', rooms: 2, baths: 1, size: 72, img: 'https://images.unsplash.com/photo-1512917774080-9991f1c4c750?w=600&q=80', type: 'Kaufen', location: 'Prenzlauer Berg', has360: false },
    { id: 4, title: 'Loft Kreuzberg', price: '2.800', rooms: 2, baths: 1, size: 85, img: 'https://images.unsplash.com/photo-1493809842364-78817add7ffb?w=600&q=80', type: 'Mieten', location: 'Kreuzberg', has360: true },
    { id: 5, title: 'Villa Grunewald', price: '2.950.000', rooms: 6, baths: 3, size: 320, img: 'https://images.unsplash.com/photo-1600596542815-ffad4c1539a9?w=600&q=80', type: 'Kaufen', location: 'Grunewald', has360: true },
    { id: 6, title: 'Maisonette Friedrichshain', price: '1.650', rooms: 3, baths: 2, size: 110, img: 'https://images.unsplash.com/photo-1600585154340-be6161a56a0c?w=600&q=80', type: 'Mieten', location: 'Friedrichshain', has360: false },
  ];

  const toggleFavorite = (id) => {
    setFavorites(prev => prev.includes(id) ? prev.filter(f => f !== id) : [...prev, id]);
  };

  const stats = [
    { value: '5.000+', label: 'Immobilien' },
    { value: '98%', label: 'Zufriedenheit' },
    { value: '24h', label: 'Antwortzeit' },
    { value: '15+', label: 'Jahre Erfahrung' },
  ];

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Back Link */}
      <div className="fixed top-4 left-4 z-50">
        <Link to="/portfolio">
          <Button variant="outline" className="bg-white shadow-md">
            <ArrowLeft size={16} className="mr-2" />
            Zurück zum Portfolio
          </Button>
        </Link>
      </div>

      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-40 bg-white/95 backdrop-blur-md shadow-sm">
        <div className="max-w-7xl mx-auto px-4 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-indigo-600 flex items-center justify-center">
              <Home className="text-white" size={20} />
            </div>
            <span className="text-slate-900 font-bold text-lg">ImmoVision</span>
          </div>
          <div className="hidden md:flex items-center gap-6 text-sm text-slate-600">
            <a href="#kaufen" className="hover:text-indigo-600">Kaufen</a>
            <a href="#mieten" className="hover:text-indigo-600">Mieten</a>
            <a href="#verkaufen" className="hover:text-indigo-600">Verkaufen</a>
            <a href="#bewertung" className="hover:text-indigo-600">Bewertung</a>
          </div>
          <div className="flex items-center gap-3">
            <Button variant="outline" className="hidden sm:flex">
              <Heart size={16} className="mr-2" />
              Merkliste ({favorites.length})
            </Button>
            <Button className="bg-indigo-600 hover:bg-indigo-700 text-white">
              Anmelden
            </Button>
          </div>
        </div>
      </nav>

      {/* Hero with Search */}
      <section className="relative pt-16 bg-gradient-to-br from-indigo-900 to-slate-900">
        <div className="absolute inset-0 bg-[url('https://images.unsplash.com/photo-1560518883-ce09059eeffa?w=1920&q=80')] bg-cover bg-center opacity-20" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24">
          <div className="text-center mb-12">
            <h1 className="text-4xl sm:text-5xl font-bold text-white mb-4">
              Finden Sie Ihr Traumzuhause
            </h1>
            <p className="text-xl text-slate-300">
              Über 5.000 Immobilien in Berlin und Umgebung
            </p>
          </div>

          {/* Search Card */}
          <Card className="max-w-4xl mx-auto shadow-2xl">
            <CardContent className="p-6">
              <div className="flex flex-wrap gap-4">
                <div className="flex-1 min-w-[200px]">
                  <label className="text-xs text-slate-500 mb-1 block">Ort oder PLZ</label>
                  <div className="flex items-center gap-2 border border-slate-200 rounded-lg px-3 py-2">
                    <MapPin size={16} className="text-slate-400" />
                    <input type="text" placeholder="Berlin, Deutschland" className="flex-1 outline-none text-sm" />
                  </div>
                </div>
                <div className="w-36">
                  <label className="text-xs text-slate-500 mb-1 block">Art</label>
                  <select className="w-full h-10 px-3 border border-slate-200 rounded-lg text-sm">
                    <option>Kaufen</option>
                    <option>Mieten</option>
                  </select>
                </div>
                <div className="w-36">
                  <label className="text-xs text-slate-500 mb-1 block">Preis bis</label>
                  <div className="flex items-center gap-2 border border-slate-200 rounded-lg px-3 py-2">
                    <Euro size={16} className="text-slate-400" />
                    <input type="text" placeholder="1.500.000" className="flex-1 outline-none text-sm w-full" />
                  </div>
                </div>
                <div className="w-28">
                  <label className="text-xs text-slate-500 mb-1 block">Zimmer</label>
                  <select className="w-full h-10 px-3 border border-slate-200 rounded-lg text-sm">
                    <option>1+</option>
                    <option>2+</option>
                    <option>3+</option>
                    <option>4+</option>
                    <option>5+</option>
                  </select>
                </div>
                <Button className="bg-indigo-600 hover:bg-indigo-700 text-white self-end h-10 px-8">
                  <Search size={18} className="mr-2" />
                  Suchen
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Stats */}
      <section className="py-8 bg-white border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {stats.map((stat, i) => (
              <div key={i} className="text-center">
                <div className="text-3xl font-bold text-indigo-600">{stat.value}</div>
                <div className="text-slate-600 text-sm">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Listings */}
      <section id="kaufen" className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center mb-8">
            <div>
              <h2 className="text-2xl font-bold text-slate-900">Top Immobilien</h2>
              <p className="text-slate-600">{properties.length} Objekte gefunden</p>
            </div>
            <div className="flex items-center gap-2">
              <Button variant={viewMode === 'grid' ? 'default' : 'outline'} size="sm" onClick={() => setViewMode('grid')} className={viewMode === 'grid' ? 'bg-indigo-600' : ''}>
                <Grid size={16} />
              </Button>
              <Button variant={viewMode === 'list' ? 'default' : 'outline'} size="sm" onClick={() => setViewMode('list')} className={viewMode === 'list' ? 'bg-indigo-600' : ''}>
                <List size={16} />
              </Button>
            </div>
          </div>

          <div className={`grid ${viewMode === 'grid' ? 'md:grid-cols-2 lg:grid-cols-3' : 'grid-cols-1'} gap-6`}>
            {properties.map((property) => (
              <Card key={property.id} className="border-0 shadow-lg overflow-hidden group hover:shadow-xl transition-all duration-300">
                <div className="relative h-56">
                  <img src={property.img} alt={property.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                  <button 
                    onClick={() => toggleFavorite(property.id)}
                    className="absolute top-3 right-3 w-10 h-10 rounded-full bg-white/90 flex items-center justify-center hover:bg-white shadow-md"
                  >
                    <Heart size={18} className={favorites.includes(property.id) ? 'text-red-500 fill-red-500' : 'text-slate-600'} />
                  </button>
                  <div className="absolute top-3 left-3 flex gap-2">
                    <span className="bg-indigo-600 text-white px-3 py-1 rounded-full text-xs font-medium">
                      {property.type}
                    </span>
                    {property.has360 && (
                      <span className="bg-amber-500 text-white px-3 py-1 rounded-full text-xs font-medium">
                        360° Tour
                      </span>
                    )}
                  </div>
                </div>
                <CardContent className="p-5">
                  <div className="flex items-center gap-2 text-slate-500 text-sm mb-2">
                    <MapPin size={14} />
                    {property.location}
                  </div>
                  <h3 className="font-bold text-slate-900 mb-2">{property.title}</h3>
                  <p className="text-2xl font-bold text-indigo-600 mb-4">
                    € {property.price}{property.type === 'Mieten' ? '/Monat' : ''}
                  </p>
                  <div className="flex gap-4 text-sm text-slate-500 mb-4">
                    <span className="flex items-center gap-1"><Bed size={14} /> {property.rooms} Zimmer</span>
                    <span className="flex items-center gap-1"><Bath size={14} /> {property.baths} Bad</span>
                    <span className="flex items-center gap-1"><Square size={14} /> {property.size} m²</span>
                  </div>
                  <Button className="w-full bg-slate-900 hover:bg-slate-800 text-white">
                    Details ansehen
                    <ChevronRight size={16} className="ml-2" />
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>

          <div className="text-center mt-12">
            <Button variant="outline" className="border-indigo-600 text-indigo-600 hover:bg-indigo-50">
              Alle Immobilien anzeigen
              <ChevronRight size={16} className="ml-2" />
            </Button>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section id="verkaufen" className="py-20 bg-indigo-600">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold text-white mb-4">Sie möchten verkaufen?</h2>
          <p className="text-indigo-100 mb-8">Erhalten Sie eine kostenlose Immobilienbewertung von unseren Experten.</p>
          <Button size="lg" className="bg-white text-indigo-600 hover:bg-indigo-50">
            Kostenlose Bewertung anfordern
          </Button>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 bg-slate-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-4 gap-8 text-slate-400">
            <div>
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-lg bg-indigo-600 flex items-center justify-center">
                  <Home className="text-white" size={20} />
                </div>
                <span className="text-white font-bold">ImmoVision</span>
              </div>
              <p className="text-sm">Ihr Partner für Immobilien in Berlin.</p>
            </div>
            <div>
              <h4 className="text-white font-semibold mb-4">Services</h4>
              <ul className="space-y-2 text-sm">
                <li>Kaufen</li>
                <li>Mieten</li>
                <li>Verkaufen</li>
                <li>Bewertung</li>
              </ul>
            </div>
            <div>
              <h4 className="text-white font-semibold mb-4">Kontakt</h4>
              <p className="text-sm">Immobilienstraße 1<br />10115 Berlin<br />Tel: 030 987 654</p>
            </div>
            <div>
              <h4 className="text-white font-semibold mb-4">Newsletter</h4>
              <div className="flex gap-2">
                <Input placeholder="E-Mail" className="bg-slate-800 border-slate-700 text-white" />
                <Button className="bg-indigo-600 hover:bg-indigo-700">OK</Button>
              </div>
            </div>
          </div>
          <div className="mt-8 pt-8 border-t border-slate-800 text-center text-slate-500 text-sm">
            © 2024 ImmoVision. Alle Rechte vorbehalten.
          </div>
        </div>
      </footer>

      {/* Demo Badge */}
      <div className="fixed bottom-4 right-4 bg-blue-600 text-white px-4 py-2 rounded-full text-sm font-medium shadow-lg z-50">
        Demo-Projekt von Beta-Webagentur
      </div>
    </div>
  );
};

export default ImmobilienDemo;
